import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import classNames from "classnames";
import styles from "./styles.module.scss";
import Loading from "components/Loading";
import PackThreadRenderer from "components/PackThreadRenderer";
import { useNavigate, useParams } from "react-router-dom";
import listService from "api/listService";
import { EOrder, ETypeOfOrder } from "components/SortOrder/typings";
import PackageThreadContext from "contexts/PackageThreadContext";
import KeysContext from "contexts/KeysContext";
import usePDF from "hooks/usePDF";
import { IAttachment } from "dto/IAttachment";
import PDFViewer from "components/PDFViewer";
import ErrorScreen from "components/ErrorScreen";
import { SignalRContext } from "../../App";
import PackagesContext from "contexts/PackagesContext";
import EmptyPanel from "components/EmptyPanel";
import { IPackage } from "dto/IPackage";
import ModalsManagerContext, {
  ContractRegistryModalOptions,
  InvoiceRegistryModalOptions,
} from "contexts/ModalsManager";
import BackIcon from "assets/icons/back.svg?react";
import { Props } from "./typings";
import { useTranslation } from "react-i18next";

const PackageThread: FC<Props> = ({
  className,
  headerClassName,
  onPreview,
  listFilter,
  setSelectedElements,
  isMobile = false,
  threadIdOverride,
  threadPackageIdOverride,
  showHeader = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const PDFId = queryParams.get("pdf");

  const { threadId: threadIdParam, threadPackageId: threadPackageIdParam } =
    useParams();

  const threadId = threadIdOverride ?? threadIdParam;
  const threadPackageId = threadPackageIdOverride ?? threadPackageIdParam;

  const {
    blob,
    create,
    isDownload,
    attachment,
    isNotSupported,
    removeBlob,
    download,
  } = usePDF();

  const virtuoso = useRef<VirtuosoHandle>(null);
  const pdfRef = useRef<HTMLDivElement>();
  const parent = useRef(null);

  // --- Scroll anchor refs ---
  // All mutable scroll state in refs — observer closures never go stale
  const shouldLockScroll = useRef<boolean>(false);
  const isProgrammaticScroll = useRef<boolean>(false);
  const initialScrollDone = useRef<boolean>(false);

  SignalRContext.useSignalREffect(
    "newMessages",
    (message: any) => {
      message.items.forEach((m) => {
        if (m.thread?.id === threadId) {
          refetch();
        }
      });
    },
    [],
  );

  const {
    data: packageData,
    refetch,
  } = listService.useGetPackage(
    { ThreadId: threadId, IncludeRemoved: !!threadIdOverride },
    listFilter ?? {
      type: ETypeOfOrder.SEND_DATE,
      order: EOrder.DESCENDING,
    },
  );

  const Packages = useMemo(() => packageData?.data.Items ?? [], [packageData]);
  const selectedPackageIndex = useMemo(
    () =>
      packageData?.data.Items
        ? packageData?.data.Items.findIndex((x) => x.Id === threadPackageId)
        : 0,
    [packageData, threadPackageId],
  );

  const { threadPackages, setThreadPackages } =
    useContext(PackageThreadContext);
  const { keys, setKeys } = useContext(KeysContext);
  const { packages } = useContext(PackagesContext);
  const { modals, setModals, setOptions } = useContext(ModalsManagerContext);

  // --- TEMPORARY DEBUG — remove after diagnosis ---
  const renderCountRef = useRef(0);
  renderCountRef.current++;
  const rc = renderCountRef.current;

  // Log every render with timestamp
  console.log(
    `[PackageThread] [PT render #${rc}] t=${performance.now().toFixed(0)}ms`,
    {
      Packages: Packages?.length,
      Key: keys.length,
      ThreadPackages: threadPackages.length,
      threadPackageId,
      shouldLock: shouldLockScroll.current,
      initialScrollDone: initialScrollDone.current,
    },
  );

  const [previewPackage, setPreviewPackage] = useState<IPackage>(null);

  const packFromContext = useMemo(
    () => packages?.find((p) => p.Thread?.Id === threadId),
    [packages, threadPackageId],
  );

  // Batch both context writes into one effect to avoid two separate render waves.
  // Previously setThreadPackages + setKeys fired separately causing double re-renders
  // which confused the ResizeObserver timing.
  useEffect(() => {
    if (!packageData?.data) return;
    setThreadPackages(packageData.data.Items ?? []);
    const newKeys = [packageData.data.ObjectKeys, packageData.data.Keys].flat();
    setKeys((prev) => [...prev, ...newKeys]);
  }, [packageData]);

  useEffect(() => {
    if (!PDFId?.length || !Packages.length) return;
    const attUri = PDFId.split(":")[2];
    const pack = Packages.find((p) =>
      p.Attachments?.find((a) => a.Uri === attUri),
    );
    if (!pack) return;
    setPreviewPackage(pack);
    const attach = pack.Attachments?.find((a) => a.Uri === attUri);
    if (attach && keys.length) {
      create(pack.Id, attach, pack.Kind);
    }
  }, [PDFId, Packages, keys]);

  useEffect(() => {
    if (pdfRef.current) {
      pdfRef.current.classList.add(styles.show);
    }
  }, [pdfRef.current]);

  // Reset lock state whenever the target package changes
  useEffect(() => {
    initialScrollDone.current = false;
    shouldLockScroll.current = false;

    if (
      virtuoso?.current &&
      !shouldLockScroll?.current &&
      Packages?.length &&
      selectedPackageIndex >= 0
    ) {
      console.log("Scrolling to selectedPackageIndex", selectedPackageIndex);
      isProgrammaticScroll.current = true;
      console.log("threadPackageId chagned!", threadPackageId);
      virtuoso.current.scrollToIndex(selectedPackageIndex);
      isProgrammaticScroll.current = false;
    }
  }, [threadPackageId]);

  const handleAddToCase = useCallback(() => {
    localStorage.setItem("package", JSON.stringify(previewPackage));
    localStorage.setItem("attach", JSON.stringify(attachment));
    setModals({ ...modals, attachCase: true, hide: false });
  }, [previewPackage, attachment, modals, setModals]);

  const handleAddToRegistry = useCallback(
    (type: "contracts" | "invoices") => {
      const fileName = attachment?.Label ?? attachment?.FileName ?? "dokument.pdf";
      if (type === "contracts") {
        const opts = new ContractRegistryModalOptions();
        opts.file = blob;
        opts.fileName = fileName;
        setOptions(opts);
        setModals({ ...modals, createContractEntry: true, hide: false });
      } else {
        const opts = new InvoiceRegistryModalOptions();
        opts.file = blob;
        opts.fileName = fileName;
        setOptions(opts);
        setModals({ ...modals, createInvoiceRegistryEntry: true, hide: false });
      }
    },
    [blob, attachment, modals, setModals, setOptions],
  );

  const handleAddToDossier = useCallback(() => {
    localStorage.setItem("move", "true");
    localStorage.setItem("package", JSON.stringify(previewPackage));
    localStorage.setItem("attach", JSON.stringify(attachment));
    setModals({ ...modals, attachCase: true, hide: false });
  }, [previewPackage, attachment, modals, setModals]);

  const onDownload = async (
    pack: IPackage,
    attachment: IAttachment,
  ) => {
    await download(pack.Id, attachment, pack.Kind);

    return;
  };

  const scrollerRef = useRef<HTMLElement>(null);

  const setScrollerRef = useCallback((node) => {
    if (node) {
      scrollerRef.current = node;
    }
  }, []);

  const handleMobileBack = () => {
    window.history.back();
  };

  useEffect(() => {
    const scroller = scrollerRef?.current;

    if (!scroller) return;

    const handleWheel = (event) => {
      console.log("wheel event", event);
      //shouldLockScroll.current = true;
    };

    const handleScroll = (event) => {
      if (isProgrammaticScroll.current) return;

      console.log("scroll event", event);
      //shouldLockScroll.current = true;
    };

    scroller.addEventListener("wheel", handleWheel, { passive: true });
    scroller.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scroller.removeEventListener("wheel", handleWheel);
    };
  }, [scrollerRef, scrollerRef.current]);

  // // Stable callback — reads everything from refs, never stale
  // const onItemAboveAnchorResized = useCallback((resizedIndex: number) => {

  // }, []); // empty deps — all values via refs

  if (isNotSupported) {
    return (
      <div className={styles.errorWrapper}>
        <div className={styles.errorContent}>
          <ErrorScreen text={t("errors.fileFormatNotSupported")} />
        </div>
      </div>
    );
  }

  // if (selectedElements.length > 1) {
  //   return <SelectedElementsPanel count={selectedElements.length} />;
  // }

  if (packFromContext?.isDelete) {
    return <EmptyPanel />;
  }

  if (!Packages?.length) {
    return <Loading />;
  }

  return (
    <div className={classNames(styles.wrapper, className)}>
      {showHeader && <div className={`${styles.header} ${headerClassName}`}>
        {isMobile && (
          <div className={styles.tools}>
            <div className={styles.backButton}>
              <BackIcon onClick={() => handleMobileBack()}></BackIcon>
            </div>
          </div>
        )}
      </div>}
      <Virtuoso
        ref={virtuoso}
        style={{
          height: "calc(100% - 75px)",
          marginTop: "8px",
          width: "calc(100% - 8px)",
        }}
        scrollerRef={setScrollerRef}
        initialTopMostItemIndex={
          selectedPackageIndex >= 0 ? selectedPackageIndex : 0
        }
        totalListHeightChanged={() => {
          if (
            virtuoso?.current &&
            !shouldLockScroll?.current &&
            selectedPackageIndex >= 0
          ) {
            console.log("totalListHeightChanged, scrolling");
            isProgrammaticScroll.current = true;
            virtuoso.current.scrollToIndex(selectedPackageIndex);
            isProgrammaticScroll.current = false;
          }
          return;
        }}
        followOutput={false}
        data={Packages}
        overscan={{ main: 600, reverse: 5000 }}
        onWheel={() => {
          // Genuine user scroll
          console.log("User is scrolling");
          shouldLockScroll.current = true;
        }}
        isScrolling={(scrolling) => {
          if (!scrolling) return; // only care about scrolling=true events

          if (isProgrammaticScroll.current) return;
        }}
        itemContent={(index, pack) => (
          <PackThreadRenderer
            key={pack.Id}
            index={index}
            parent={parent}
            onPreview={onPreview}
            onDownload={onDownload}
            refetch={refetch}
            pack={pack}
            isFirst={index === 0}
            isLast={index === Packages.length - 1}
            setSelectedElements={setSelectedElements}
          />
        )}
      />

      {blob && !!PDFId?.length && !isDownload && (
        <div className={styles.pdfW} ref={pdfRef}>
          <PDFViewer
            title={attachment?.Label ?? attachment?.FileName ?? ""}
            file={blob}
            onCloseButton={() => {
              pdfRef.current.classList.remove(styles.show);
              pdfRef.current.classList.add(styles.hide);
              setTimeout(() => {
                removeBlob();
                const url = new URL(window.location.href);
                url.searchParams.delete("pdf");
                navigate(`${url.pathname}${url.search}`);
              }, 1000);
            }}
            handleAddToCase={!previewPackage.Case ? handleAddToCase : undefined}
            handleAddToDossier={
              previewPackage.Case ? handleAddToDossier : undefined
            }
            handleAddToRegistry={handleAddToRegistry}
          />
        </div>
      )}
    </div>
  );
};

export default PackageThread;
